import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Target,
  TrendingUp,
  Award,
  Layers,
  Activity,
  Crown,
  CheckCircle2,
  Users,
  Globe2,
  Building2,
} from "lucide-react";
import { PageHeader } from "../components/Section";
import { TRAINING_PROGRAMS } from "../data/site";

const ICONS = {
  "yellow-belt": Target,
  "green-belt": TrendingUp,
  "black-belt": Award,
  "lean-foundations": Layers,
  "lean-practitioner": Activity,
  "lean-leadership": Crown,
};

const DELIVERY_MODES = [
  {
    icon: Users,
    title: "Public programmes",
    body: "Open-enrolment cohorts running monthly across Hong Kong, Singapore and online — bring one or two practitioners and learn alongside other organisations.",
  },
  {
    icon: Globe2,
    title: "Live online",
    body: "Instructor-led virtual classrooms with breakout exercises, Minitab labs and small-cohort coaching. Identical certification standard, zero travel.",
  },
  {
    icon: Building2,
    title: "On-site corporate",
    body: "Tailored cohorts at your premises, anchored to a sponsored project portfolio so certification translates straight into measured benefit.",
  },
];

const TrainingCard = ({ p }) => {
  const Icon = ICONS[p.id] || Target;
  return (
    <article
      id={p.id}
      data-testid={`training-${p.id}`}
      className="bg-white p-10 lg:p-12 flex flex-col"
    >
      <div className="flex items-start justify-between mb-8">
        <Icon className="w-8 h-8 text-[#E63946]" strokeWidth={1.5} />
        <div className="text-right">
          <p className="font-mono-tech text-[11px] tracking-widest text-zinc-500">
            {p.code}
          </p>
          <p className="mt-1 text-[11px] tracking-[0.18em] uppercase text-zinc-500">
            {p.level}
          </p>
        </div>
      </div>

      <h3 className="font-display text-3xl lg:text-4xl font-black tracking-tighter text-black">
        {p.title}
      </h3>
      <p className="mt-3 text-xs tracking-[0.18em] uppercase text-zinc-500">{p.duration}</p>

      <p className="mt-6 text-sm leading-relaxed text-zinc-700">{p.description}</p>

      <p className="mt-6 text-xs tracking-[0.2em] uppercase text-zinc-500 font-semibold">
        Audience
      </p>
      <p className="mt-2 text-sm text-zinc-700">{p.audience}</p>

      <p className="mt-6 text-xs tracking-[0.2em] uppercase text-zinc-500 font-semibold">
        Learning outcomes
      </p>
      <ul className="mt-3 space-y-2.5">
        {p.outcomes.map((o) => (
          <li key={o} className="flex items-start gap-2.5 text-sm text-zinc-700">
            <CheckCircle2 className="w-4 h-4 text-[#E63946] flex-shrink-0 mt-0.5" strokeWidth={1.75} />
            <span className="leading-relaxed">{o}</span>
          </li>
        ))}
      </ul>

      <Link
        to="/contact"
        data-testid={`training-cta-${p.id}`}
        className="group mt-10 inline-flex items-center gap-2 self-start text-sm font-semibold text-black border-b border-black hover:text-[#E63946] hover:border-[#E63946] pb-1 transition-colors"
      >
        Enquire about this programme
        <ArrowUpRight className="w-4 h-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
      </Link>
    </article>
  );
};

const Training = () => (
  <div data-testid="page-training">
    <PageHeader
      overline="Training"
      title={<>Lean Six Sigma. <span className="text-[#E63946]">Certifiable.</span> Practical.</>}
      kicker="Six internationally aligned programmes — from Yellow Belt to Lean Leadership — designed for real workplaces and real outcomes. Delivered by senior practitioners across the Asia-Pacific."
    />

    <section className="border-b border-black/10 bg-[#F7F7F8]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 lg:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-black/10 border border-black/10">
          {TRAINING_PROGRAMS.map((p) => (
            <TrainingCard key={p.id} p={p} />
          ))}
        </div>
      </div>
    </section>

    <section className="border-b border-black/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 lg:py-32">
        <div className="max-w-3xl mb-14">
          <p className="text-xs tracking-[0.22em] uppercase text-[#E63946] font-semibold mb-6">
            How we deliver
          </p>
          <h2 className="font-display text-3xl lg:text-5xl font-black tracking-tighter leading-[1.05]">
            Three formats. <span className="text-zinc-500">One certification standard.</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-black/10 border border-black/10">
          {DELIVERY_MODES.map((m) => (
            <div
              key={m.title}
              data-testid={`delivery-${m.title.toLowerCase().replace(/\s+/g, "-")}`}
              className="bg-white p-10 lg:p-12"
            >
              <m.icon className="w-7 h-7 text-[#E63946]" strokeWidth={1.5} />
              <h3 className="mt-8 font-display text-2xl font-bold tracking-tight text-black">
                {m.title}
              </h3>
              <p className="mt-4 text-sm leading-relaxed text-zinc-600">{m.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="bg-[#09090B] text-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 lg:py-32 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-8">
          <p className="text-xs tracking-[0.22em] uppercase text-[#E63946] font-semibold mb-6">
            Corporate cohorts
          </p>
          <h2 className="font-display text-4xl lg:text-6xl font-black tracking-tighter leading-[1.02]">
            Train a team. Build a capability.
          </h2>
          <p className="mt-6 text-zinc-400 max-w-2xl text-base lg:text-lg leading-relaxed">
            Most of our certifications are delivered as corporate cohorts — anchored to a
            project portfolio that delivers measured benefits during the programme itself.
          </p>
        </div>
        <div className="lg:col-span-4 flex lg:justify-end">
          <Link
            to="/contact"
            data-testid="training-corporate-cta"
            className="group inline-flex items-center gap-2 bg-[#E63946] text-white px-7 py-4 text-sm font-semibold hover:bg-[#D32F2F] transition-colors"
          >
            Request a cohort <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  </div>
);

export default Training;
